/**
 * The floating chrome over the canvas: the tool rail. What used to be the
 * status capsule now lives in the explorations sidebar
 * (ui/ExplorationsSidebar.tsx) — its list and its models row restate everything
 * the capsule knew — and the old library rail is gone entirely: the sidebar
 * carries its daily jobs, and the palette carries the rest.
 *
 * The rail wears the shell the panes do (`GlassShell`), flat on the field as
 * the design draws its dock; transient layers — the eye popover, the spatial
 * view's opening caption — take `glass()` inline, as cards. Those are two
 * SHAPES of one material, not a heavier and a lighter glass: both read
 * `--glass-fill` and neither may restate it. It is vertically centred on the
 * canvas edge, and slides with the inspector's inset the way every other
 * floating piece does.
 */

import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactElement } from 'react'
import { useStore } from '../core/store'
import type { Theme } from '../core/store'
import { channelOf, type Channel, type Filters } from '../core/types'
import { CHANNEL_KEYS, hiddenFindings, passesFilters } from '../core/visibility'
import {
  CHANNEL_WORD,
  ENDING_LABEL,
  OBJECTION_STOOD_BELOW,
  READING_LABEL,
} from '../core/vocabulary'
import { Icon } from './components/Icon'
import type { IconName } from './components/Icon'
import { Switch } from './components/Controls'
import { glass } from './components/glass'
import { hoverFill, useHover } from './components/hover'
import { GlassShell } from './components/GlassShell'
import { useEscapeToClose } from './components/useEscapeToClose'
import { useMedia } from './components/useMedia'
import { useLiveCoach } from './coachView'
import { useAdjustOpen } from './adjust'

/* ── Right-edge tool rail ─────────────────────────────────────────────── */

function DockBtn({
  name,
  label,
  onClick,
  theme,
  active,
  opensDialog,
  disabled,
  why,
  keys,
  tint = 'var(--sem-insight)',
}: {
  name: IconName
  label: string
  onClick: () => void
  theme: Theme
  active?: boolean
  /** True when `active` means "the popover this button opens is on screen". The
   *  eye is the only one. It paints pressed the way the cube does, but a dialog
   *  is expanded rather than pressed, and announcing both says one fact twice. */
  opensDialog?: boolean
  disabled?: boolean
  /** Why it is disabled, said on the hover, to a screen reader, and as a toast
   *  when it is pressed anyway — a greyed spark with its own name for a tooltip
   *  told nobody what would light it, and a tooltip tells a thumb nothing. */
  why?: string
  /** Its key: printed after the label on the hover, and given to a screen reader
   *  as a shortcut rather than read as the last word of the button's name. */
  keys?: { shown: string; aria: string }
  tint?: string
}) {
  const [hov, bind] = useHover()
  const toast = useStore((s) => s.toast)
  const hint = keys ? `${label}  ${keys.shown}` : label
  return (
    <button
      {...bind}
      // Shut, not disabled (D335), as the coach's own Back and Next are: a
      // `disabled` button fires no click, and on a phone there is no hover to
      // carry the title — so a tap on the greyed spark did nothing and said
      // nothing. The press now says why, in the words the hover uses.
      className="dockbtn"
      onClick={() => {
        if (disabled) {
          if (why) toast(why, 'info')
          return
        }
        onClick()
      }}
      aria-disabled={disabled || undefined}
      aria-label={label}
      aria-keyshortcuts={keys?.aria}
      aria-description={disabled && why ? why : undefined}
      aria-pressed={opensDialog ? undefined : active}
      aria-expanded={opensDialog ? Boolean(active) : undefined}
      aria-haspopup={opensDialog ? 'dialog' : undefined}
      title={disabled && why ? `${hint} — ${why}` : hint}
      // The size is the stylesheet's (`.dockbtn`): 36 by default and the 40px
      // floor every other control gets on a coarse pointer, which an inline size
      // here would have outranked.
      // The corner sits inside the dock's 10 at its 4px inset, as the design's
      // buttons do (D357); the medium corner stood proud of the dock's own.
      style={{
        display: 'grid',
        placeItems: 'center',
        borderRadius: 7,
        color: disabled ? 'var(--text-3)' : active ? tint : hov ? 'var(--text-1)' : 'var(--text-2)',
        background: active
          ? `color-mix(in srgb, ${tint} 14%, transparent)`
          : hoverFill(theme, hov && !disabled),
        boxShadow: active ? `inset 0 0 0 1px color-mix(in srgb, ${tint} 35%, transparent)` : 'none',
        transition: 'background 130ms ease, color 130ms ease',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <Icon name={name} size={15} />
    </button>
  )
}

const EYEBROW: CSSProperties = {
  fontSize: 'var(--fs-micro)',
  fontWeight: 590,
  letterSpacing: 'var(--tracking-caps)',
  textTransform: 'uppercase',
  color: 'var(--text-3)',
}

/**
 * The legend's channels, with what each colour means. One line per hue, in the
 * same order as the bottom key — and the descriptions are the sheet's, reworded
 * only where this app's vocabulary already had the word (a "contradiction" is
 * the graph arguing with itself; "disputed" is the world doing it).
 *
 * The signal channel is ARGUED, not supported. `kindChannelOf` puts every claim
 * the model rated 0.4 or higher there, checked or not, so "Supported — holds up
 * so far" put the one word this app reserves for a source reading on output
 * nothing had ever checked. Support belongs to stored readings and recorded
 * results; the filter key stays `supported` so no saved filter changes.
 *
 * The labels are `CHANNEL_WORD`, the status table's row for each hue, which the
 * step rail reads too — the legend keys the colours in the words everything
 * else prints, rather than keeping a copy that has to be held in step.
 */
export const LEGEND: Array<{ label: string; ch: Channel; filter: keyof Filters; d: string }> = [
  {
    label: CHANNEL_WORD.signal,
    ch: 'signal',
    filter: 'supported',
    // A claim you rewrote lands here too, whatever the model rated the words it
    // replaced (`kindChannelOf`): nothing rated yours.
    d: 'The model rated it 0.4 or higher, or you rewrote it. Not checked unless it shows a source reading.',
  },
  {
    label: CHANNEL_WORD.tension,
    ch: 'tension',
    filter: 'contradictions',
    d: 'Conflicts with another claim on the board.',
  },
  {
    label: CHANNEL_WORD.gap,
    ch: 'gap',
    filter: 'gaps',
    d: 'A hole in the reasoning, not yet filled.',
  },
  {
    label: CHANNEL_WORD.insight,
    ch: 'insight',
    filter: 'insights',
    // The channel, not a kind: insights, takeaways and syntheses all wear this hue
    // (`kindChannelOf`), so the row says all three rather than let "Insight" read
    // as the insight cards alone (D333).
    d: 'Insights, takeaways and syntheses: a testable move the graph did not start with.',
  },
  {
    label: CHANNEL_WORD.refuted,
    ch: 'refuted',
    filter: 'disputed',
    d: 'The world disagrees — sources say no.',
  },
  { label: CHANNEL_WORD.weak, ch: 'weak', filter: 'weak', d: 'The model rated it below 0.4.' },
  // One channel, three endings: the grey is every path the reasoning closed,
  // and the card names which of the three it reached (`core/endings.ts`).
  {
    label: CHANNEL_WORD.dead,
    ch: 'dead',
    filter: 'dead',
    // The three endings in the card's own words, read from the table the card
    // reads, so the key cannot go on naming an ending the card has renamed.
    d: `Closed in reasoning — “${ENDING_LABEL['objection-stood']}”, “${ENDING_LABEL['path-failed']}” or “${ENDING_LABEL.open}”.`,
  },
]

/**
 * The hover on a legend row's toggle. It names what the press hides, and for the
 * insight channel that is more than the insight cards: takeaways and syntheses
 * wear the hue and go with it. "Hide insight nodes" would name the conjecture
 * kind alone — one word, two meanings, on one press (D333).
 */
export function legendToggleTitle(row: { label: string; ch: Channel }, on: boolean): string {
  const what =
    row.ch === 'insight' ? 'insights, takeaways and syntheses' : `${row.label.toLowerCase()} nodes`
  return `${on ? 'Hide' : 'Show'} ${what} on the canvas`
}

/**
 * The canvas's marks, at legend size.
 *
 * Deliberately not in `Icon.tsx`: that set is chrome — one 16-unit grid, one
 * 1.5 stroke, eighteen glyphs — and these are the canvas's own vocabulary,
 * drawn against `Renderer.ts` rather than against the icon grid. A 22-unit grid,
 * because a node and the fold chevrons that sit nearly four radii out of it both
 * have to fit inside one swatch.
 *
 * Neutral ink throughout. On the canvas the glyph carries the STATE and the hue
 * carries the channel, so a swatch in a channel colour would be asserting a
 * binding that is not there. Two exceptions, each a binding that is there: the
 * rewrite ring, which the renderer really does draw in `p.accent`, and the
 * struck disc, whose chords are drawn on exactly the claims `liveChannelOf`
 * puts in the refuted hue — so its swatch wears that hue, and neutral ink
 * would be the swatch hiding the binding.
 *
 * The closed path's key is `closed`, its row's own word. It was `retracted`,
 * after the renderer's "retraction bulb", while the row said "A path closed" and
 * the word "retracted" came to mean a publisher withdrawing a cited work — which
 * now has a mark of its own, `citesRetracted`, one row down from the rim it
 * breaks.
 */
type MarkName =
  | 'disc'
  | 'ring'
  | 'crossed'
  | 'haloed'
  | 'conjecture'
  | 'rewritten'
  | 'struck'
  | 'checked'
  | 'citesRetracted'
  | 'closed'
  | 'notched'
  | 'folded'
  | 'taper'
  | 'withering'
  | 'detached'
  | 'depth'

const MARK: Record<MarkName, ReactElement> = {
  disc: <circle cx="11" cy="11" r="4.6" fill="currentColor" stroke="none" />,
  // Wider than the filled disc, and thick: the renderer strokes at 0.42r on a
  // 1.12r circle, so an open question reads BIGGER than a settled move.
  ring: <circle cx="11" cy="11" r="4.8" strokeWidth="1.9" />,
  // Struck through and well past the rim — the way paper draws it, and further
  // out than paper does. The night field knocks the cross out from inside the
  // disc instead, which a swatch cannot do: a knockout is painted in the ground
  // colour and this glyph has no ground of its own, so an X in the disc's own
  // ink would be an X you cannot see. What is legible is the four arms outside
  // it, so the disc is small and the arms are long.
  crossed: (
    <>
      <circle cx="11" cy="11" r="3.6" fill="currentColor" stroke="none" />
      <path d="M6.2 6.2 15.8 15.8M15.8 6.2 6.2 15.8" strokeWidth="1.5" />
    </>
  ),
  haloed: (
    <>
      <circle cx="11" cy="11" r="3.4" fill="currentColor" stroke="none" />
      <circle cx="11" cy="11" r="7.3" strokeWidth="1" />
    </>
  ),
  // The two marks above, composed the way the renderer composes them: the open
  // question's hollow centre inside the insight's containing ring. What an
  // untested conjecture is — a proposal with nothing at its middle yet.
  conjecture: (
    <>
      <circle cx="11" cy="11" r="3.4" strokeWidth="1.6" />
      <circle cx="11" cy="11" r="7.3" strokeWidth="1" />
    </>
  ),
  // Only the ring is the accent. On the canvas the disc under it keeps its own
  // channel hue, so tinting the whole swatch would say a rewrite recolours the
  // node — it does not; it adds a mark and leaves the reading alone.
  rewritten: (
    <>
      <circle cx="11" cy="11" r="2.8" fill="currentColor" stroke="none" />
      <circle cx="11" cy="11" r="7.8" stroke="var(--accent)" strokeDasharray="2 2.6" />
    </>
  ),
  // Two parallel chords across the disc, the proofreader's deletion: both
  // renderers draw them across the node's spoke in `hot()` of the refuted hue,
  // lighter on the night field and denser on paper. Here in `--text-1`, which
  // goes the same way in each theme a step further: the hue's own ink sat at
  // 1.7:1 on the disc on paper, and at legend size the pair vanished into it.
  // Wider apart than the canvas sets them (a third of the radius), where at 22
  // units they closed into one bar; and tilted as a node below and right of the
  // seed wears them, because upright in a disc the pair is a pause button.
  struck: (
    <>
      <circle cx="11" cy="11" r="5.6" fill="var(--sem-refuted)" stroke="none" />
      <path
        d="M6.97 12.91 12.91 6.97M9.09 15.03 15.03 9.09"
        stroke="var(--text-1)"
        strokeWidth="1.4"
      />
    </>
  ),
  // The disc's own edge drawn crisp: on the canvas the contour is the hot end of
  // the node's ink over a softer fill, which a one-ink swatch says with a fill
  // held back and an edge at full strength.
  checked: <circle cx="11" cy="11" r="4.8" fill="currentColor" fillOpacity="0.42" />,
  // The same contour lifted clear of the disc and broken open toward the premise
  // — to the left here, opposite the closed path's bar, which sits outward — as
  // `canvas/rim.ts` draws it: about 90° open, half a radius again out from the disc.
  citesRetracted: (
    <>
      <circle cx="11" cy="11" r="3.8" fill="currentColor" fillOpacity="0.42" stroke="none" />
      <path d="M6.45 6.5A6.4 6.4 0 1 1 6.45 15.5" strokeWidth="1.3" />
    </>
  ),
  // A ring, and one bar across the spoke outside it at the same weight — what
  // the renderer draws. Nothing here is crossed out; docs/DESIGN.md says
  // otherwise and the pixels win. No disc inside, because by the time this mark
  // appears the node beneath has drained to about a fifth of its alpha.
  closed: (
    <>
      <circle cx="9.8" cy="11" r="5" />
      <path d="M16.6 7v8" />
    </>
  ),
  // The disc whole but for the notch both renderers cut in (`canvas/notch.ts`):
  // about sixty-five degrees open, six tenths of the radius deep, three eighths
  // of a turn from the outward spoke — to the lower left, as a node right of the
  // seed wears it. Filled, and nothing across it: the claim still stands.
  notched: (
    <path d="M5.55 12.3A5.6 5.6 0 1 1 9.7 16.45L9.42 12.58Z" fill="currentColor" stroke="none" />
  ),
  // Two chevrons, the outer one quieter, arms folding back toward the node —
  // they sit on the branch's own spoke, in exactly the space the fold freed.
  folded: (
    <>
      <circle cx="5.4" cy="11" r="2.9" fill="currentColor" stroke="none" />
      <path d="M11.6 7.4 15 11l-3.4 3.6" strokeWidth="1.3" />
      <path d="M15.8 7.4 19.2 11l-3.4 3.6" strokeWidth="1.3" opacity="0.62" />
    </>
  ),
  // Thick at the premise, thin at the claim, ending on the node it lands on.
  // Roughly 0.3 across, which is the renderer's own tip ratio.
  taper: (
    <g fill="currentColor" stroke="none">
      <path d="M1 8.3 15.8 10.2v1.6L1 13.7Z" />
      <circle cx="18.4" cy="11" r="2.4" />
    </g>
  ),
  // Four steps rather than a gradient. A `<defs>` id inside a component rendered
  // once per row is ten colliding ids, and withering drains the width and the
  // alpha together, which steps carry honestly. None of them is degenerate — a
  // zero-height quad is a hairline on one engine and nothing at all on the next.
  withering: (
    <g fill="currentColor" stroke="none">
      <path d="M1 8.7 6 9.25 6 12.75 1 13.3Z" />
      <path d="M6 9.25 11 9.8 11 12.2 6 12.75Z" opacity="0.6" />
      <path d="M11 9.8 15.8 10.3 15.8 11.7 11 12.2Z" opacity="0.32" />
      <path d="M15.8 10.3 20 10.72 20 11.28 15.8 11.7Z" opacity="0.13" />
    </g>
  ),
  // Two strand segments with a literal gap, matching the renderer's severed
  // premise treatment. The text beside it carries the meaning; the break is a
  // spatial reinforcement, never the only signal.
  detached: (
    <g fill="currentColor" stroke="none">
      <path d="M1 8.6 8.2 9.7v2.6L1 13.4Z" />
      <path d="M13.2 10.25 18.2 10.7v.6l-5 .45Z" opacity="0.55" />
      <circle cx="19.5" cy="11" r="1.6" />
    </g>
  ),
  // The seed at the centre, because the rings are counted outward from it.
  depth: (
    <>
      <circle cx="11" cy="11" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="11" cy="11" r="3.6" strokeWidth="0.9" />
      <circle cx="11" cy="11" r="6.6" strokeWidth="0.9" />
      <circle cx="11" cy="11" r="9.6" strokeWidth="0.9" />
    </>
  ),
}

function Mark({ name, tint }: { name: MarkName; tint?: string }) {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ color: tint ?? 'var(--text-2)', flex: 'none', marginTop: -2 }}
    >
      {MARK[name]}
    </svg>
  )
}

type MarkRow = { name: MarkName; label: string; d: string; tint?: string }

/**
 * What each mark means. The meanings are docs/DESIGN.md's own second column, so
 * the popover and the doc teach one vocabulary — but the NAMES are written
 * against what `Renderer.ts` draws, which is not always what the doc claims. The
 * doc calls the retraction a "crossed-out ring with a bar"; the renderer draws a
 * plain ring and one bar, and the cross belongs to contradictions.
 */
export const MARKS: Array<{ head: string; rows: MarkRow[] }> = [
  {
    head: 'Nodes',
    rows: [
      {
        name: 'disc',
        label: 'Filled disc',
        d: 'An established move.',
      },
      {
        name: 'ring',
        label: 'Hollow ring',
        d: 'An open question.',
      },
      {
        name: 'crossed',
        label: 'Crossed disc',
        d: 'A contradiction — the graph arguing with itself.',
      },
      {
        name: 'haloed',
        label: 'Haloed disc',
        d: 'A takeaway, or a realised synthesis.',
      },
      {
        name: 'conjecture',
        label: 'Haloed hollow ring',
        d: 'An insight — a proposal with a prediction, which nothing outside the model has tested yet. A recorded result fills it in.',
      },
      {
        name: 'rewritten',
        label: 'Dashed ring',
        // Its size and glow are said here because they are an absence: the disc
        // inside the ring is drawn unrated (`drawnRatingsOf`), and a reader who
        // learned "size is what it earns" would read the middle size as a verdict.
        d: 'A node you rewrote yourself — in the accent, the one colour here that means you did this. Drawn at a middle size with no glow: nothing rated your words.',
      },
      {
        // In the reading's own words, and first of the three marks a look from
        // outside the graph leaves, in docs/design.md's order. A recorded result
        // strikes the disc too: it outranks a source (`liveChannelOf`).
        name: 'struck',
        label: 'Disc struck through twice',
        d: `${READING_LABEL.refuted} — or a result you recorded went against it. The world disagreeing, where the crossed disc is the graph arguing with itself.`,
      },
      {
        name: 'checked',
        label: 'Crisp edge at the rim',
        d: 'Checked against sources — somebody looked. Most claims never are, and that is not a fault.',
      },
      {
        // Beside the rim it breaks, because it is that rim: the check happened,
        // and part of what it read has been withdrawn. The card says which work.
        name: 'citesRetracted',
        label: 'Rim broken open',
        d: 'The source check cites a work since retracted. That is a publisher withdrawing a record, not a verdict on the claim.',
      },
      {
        // Not "retracted": that word is a publisher withdrawing a cited work —
        // the coach's cue, the status clause, the " — RETRACTED" on a passage's
        // title, and the broken rim above — and this mark is drawn on no such
        // claim. The renderer's "retraction bulb" is anatomy, not copy.
        name: 'closed',
        label: 'Ring with a bar across it',
        d: 'A path closed — the ending was reached, not merely trailed off.',
      },
      {
        // The parent's half of an ending, in the status table's own words: the
        // closed ring above is the objection's mark, and this is the claim it
        // stood against. Here and not beside the struck disc, because it is the
        // graph arguing with itself, which never says refuted (D140).
        name: 'notched',
        label: 'Notch cut into the disc',
        d: `${OBJECTION_STOOD_BELOW.charAt(0).toUpperCase()}${OBJECTION_STOOD_BELOW.slice(1)} — a crossed disc beyond it closed its path against this claim; the card names which. The graph's own argument, not a finding from outside it.`,
      },
      {
        name: 'folded',
        label: 'Outward chevrons',
        d: 'A branch you have folded away; the count is on the caption.',
      },
    ],
  },
  {
    head: 'Strands',
    rows: [
      {
        name: 'taper',
        label: 'Strand taper',
        d: 'Direction of derivation — thick at the premise, thin at the claim.',
      },
      {
        name: 'withering',
        label: 'Strand fading to nothing',
        d: 'A path closed in reasoning — the card says whether an objection stood or the path failed.',
      },
      {
        name: 'detached',
        label: 'Strand detached at its premise',
        d: 'Reasoned before a recorded result or newer source record above it arrived. Open the claim for the exact notice.',
      },
    ],
  },
  {
    head: 'The field',
    rows: [
      {
        name: 'depth',
        label: 'Faint concentric rings',
        d: 'Depth of reasoning from the seed.',
        tint: 'var(--text-3)',
      },
    ],
  },
]

const MARK_GROUP: CSSProperties = {
  ...EYEBROW,
  display: 'block',
  fontSize: 'var(--fs-nano)',
  letterSpacing: '0.11em',
  padding: '9px 10px 3px',
}

/**
 * What the spatial view re-binds, said in the view.
 *
 * docs/DESIGN.md promises "the legend says so in the view" and until now the
 * legend did not: this popover was byte-identical in both. Somebody who learned
 * "size is novelty and confidence" on the flat canvas then reads distance as
 * importance, which is the one misreading the re-binding exists to prevent.
 */
const SPATIAL_REBIND =
  'Node size and strand thickness are distance from you here, and nothing else — under perspective, apparent size is distance. Confidence moves to strand opacity, and height is how much outside contact a claim has had. Hue, glyphs and the taper are unchanged.'

/** Where the rail's top stands on a phone, below the corner's chrome — and so
 *  where the legend hangs from there (see both below). */
const NARROW_TOP = 92

/** The same fact in one breath, for somebody who has not opened the legend. */
const SPATIAL_CAPTION =
  'Size is distance from you in this view, not importance. Confidence is in the strand opacity now, and height is outside contact.'

export function ToolDock({ stageW, stageH }: { stageW: number; stageH: number }) {
  // The same seam the inspector and the transport read — one query, so the three
  // agree about where a phone begins.
  const narrow = useMedia('(max-width: 767px)')
  const theme = useStore((s) => s.theme)
  const setTheme = useStore((s) => s.setTheme)
  const viewMode = useStore((s) => s.viewMode)
  const setViewMode = useStore((s) => s.setViewMode)
  // Through `liveCoach`, like every other coach surface. The spark's `active`
  // state is chrome about a card, and a coach stamped with another session puts
  // no card on screen — so reading the raw field here would light the dock for a
  // guide the reader cannot see, which is the exact resurrection that helper
  // exists to prevent.
  const coachOpen = useLiveCoach()?.open ?? false
  const busy = useStore((s) => s.busy)
  const runPhase = useStore((s) => s.run?.phase)
  // Rewound is a disabled state, not a refusal sentence: every coach surface is
  // hidden behind the playhead gate, so `openCoach` refuses there — and a lit
  // button whose press quietly does nothing reads as broken. Grey it instead.
  const rewound = useStore((s) => s.playhead !== null && s.playhead < s.session.order.length)
  const openCoach = useStore((s) => s.openCoach)
  const setRightTab = useStore((s) => s.setRightTab)
  const toast = useStore((s) => s.toast)
  const zoomBy = useStore((s) => s.zoomBy)
  const frame = useStore((s) => s.frame)
  const dollyCam = useStore((s) => s.dollyCam)
  const frameCam = useStore((s) => s.frameCam)
  const setFocus = useStore((s) => s.setFocus)
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const filters = useStore((s) => s.filters)
  const toggleFilter = useStore((s) => s.toggleFilter)
  const showAllChannels = useStore((s) => s.showAllChannels)
  const weighting = useStore((s) => s.weighting)
  const setWeighting = useStore((s) => s.setWeighting)
  const setPalette = useStore((s) => s.setPalette)
  const [visOpen, setVisOpen] = useState(false)
  // The marks are REFERENCE, not chrome, so they start closed — the same rule
  // `Disclosure` states for itself. The hue rows above them each toggle a
  // filter; these toggle nothing and are read once.
  const [marksOpen, setMarksOpen] = useState(false)
  const marksId = useId()
  // The popover is taller than its 560px window once the marks are out, so the
  // section opens below the fold — and a disclosure that appears to do nothing
  // reads as broken. Bring its head into view on the frame after it renders.
  const marksRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!marksOpen) return
    const el = marksRef.current
    if (!el) return
    const raf = requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest' }))
    return () => cancelAnimationFrame(raf)
  }, [marksOpen])
  // The spatial view's opening caption. Session state rather than stored: what
  // it has to survive is one visit to the view, not one install — the eye's
  // legend is the copy of record and it is always a click away. Opening that
  // legend counts as seen, because it is the same sentence read on purpose.
  const [spatialNoteSeen, setSpatialNoteSeen] = useState(false)
  // The same Escape contract the chip menus and the sidebar's own popovers
  // wear: the open popover consumes the keystroke before the ladder can act on the
  // layer beneath it — without this, Escape left the legend open and silently
  // destroyed the selection under it.
  useEscapeToClose(visOpen, () => setVisOpen(false))
  // The stage runs under the floating panes; zoom about the centre of what the
  // user can actually see, not of the element — and sit left of the inspector's
  // glass rather than under it.
  const viewInset = useStore((s) => s.viewInset)
  const cx = viewInset.left + (stageW - viewInset.left - viewInset.right) / 2

  // On a phone the dials under Adjust stand in the composer's column, and at 390
  // or 360 that column is the screen's whole width from the corner chrome down:
  // the panel's glass ran over this rail's band, the rail's top stood above the
  // panel's corner and its lower buttons showed through the glass. Raising the
  // composer over the rail (App.tsx) kept the dials pressable and left the rail
  // drawn through them. So the rail steps aside while the dials are open, the
  // way the replay dock steps in (D158): nothing on it is part of choosing the
  // next run, and it is back the moment Adjust closes. Hidden rather than
  // unmounted, so the eye's legend keeps its place; the legend closes with it.
  // The tune panel — "Tune the next run", which a brief chip opens at the
  // standing brief's list — stands in the same column and was not raised at all:
  // the rail was drawn over its right edge, and took the presses of the ✕ beside
  // a directive. It yields to that panel for the same reason.
  const adjusting = useAdjustOpen()
  const tuning = useStore((s) => s.tuneOpen)
  const yielding = narrow && (adjusting || tuning)
  useEffect(() => {
    if (yielding) setVisOpen(false)
  }, [yielding])

  const spatial = viewMode === '3d'

  // What the switches are hiding right now. The direct count — descendants of a
  // hidden node go with it, and the title says so rather than the number
  // pretending to include them.
  const switchedOff = useMemo(() => {
    let n = 0
    for (const id of session.order) {
      const node = session.nodes[id]
      if (node && node.parentId !== null && !passesFilters(node, filters)) n++
    }
    return n
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, filters, rev])
  const behindASwitch = useMemo(
    () => hiddenFindings(session, filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, filters, rev],
  )
  // Any colour row off, whether or not it is hiding anything yet: a switch off
  // over an empty channel still hides the next node that lands in it.
  const channelsOff = CHANNEL_KEYS.some((k) => !filters[k])
  // Said once, seated by the rows below: under the Disputed row when that is
  // the switch hiding them, otherwise after the colour rows, before Canvas.
  const disputedWarning =
    behindASwitch > 0 ? (
      <p
        style={{
          margin: '4px 10px 6px',
          fontSize: 'var(--fs-micro)',
          lineHeight: 1.5,
          color: 'var(--ink-refuted)',
          textWrap: 'pretty',
        }}
      >
        {behindASwitch === 1
          ? 'One node a source disputes is hidden by a switch above.'
          : `${behindASwitch} nodes a source disputes are hidden by the switches above.`}
      </p>
    ) : null
  // One count per hue, for the legend rows — the number beside each meaning.
  const perChannel = useMemo(() => {
    const n: Record<Channel, number> = {
      signal: 0,
      weak: 0,
      tension: 0,
      gap: 0,
      insight: 0,
      refuted: 0,
      dead: 0,
    }
    for (const id of session.order) {
      const node = session.nodes[id]
      if (node && node.parentId !== null) n[channelOf(node)]++
    }
    return n
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, rev])

  return (
    <div
      className="tooldock"
      style={{
        position: 'absolute',
        right: viewInset.right + 14,
        // Centred on the field where there is room, but anchored below the
        // corner's chrome on a phone. Vertically centred at 812 the rail runs to y≈550 and the
        // composer's ASK button starts at 515 — the dock covered half of the one
        // control that starts the loop, and a thumb landing there got nothing
        // with no cursor to explain it. Anchoring the top keeps the whole rail
        // in the field's upper half, clear of the composer and its plinth.
        ...(narrow
          ? { top: NARROW_TOP, transform: 'none' }
          : { top: '50%', transform: 'translateY(-50%)' }),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 10,
        transition: 'right 380ms cubic-bezier(0.22, 1, 0.36, 1)',
        zIndex: 2,
        // Out of sight, out of the tab order and out of a screen reader's list.
        visibility: yielding ? 'hidden' : undefined,
      }}
    >
      {/* One cluster, in the sheet's order: zoom in, zoom out, fit, a divider,
          then the ways of looking — spatial, the field, visibility — and the
          inspector toggle this app adds. A relative wrapper carries the badge
          and the popover as SIBLINGS of the glass: both overhang its bounds,
          and the glass clips its own children to its corners. */}
      <div style={{ position: 'relative' }}>
        {/* The design's dock: a 10px corner, 4px in from the edge, no drop. */}
        <GlassShell radius={10} lift="flat" contentStyle={{ gap: 2, padding: 4 }}>
          <DockBtn
            name="plus"
            label={spatial ? 'Move closer' : 'Zoom in'}
            theme={theme}
            onClick={() => (spatial ? dollyCam(0.8) : zoomBy(1.25, cx, stageH / 2))}
          />
          <DockBtn
            name="minus"
            label={spatial ? 'Pull back' : 'Zoom out'}
            theme={theme}
            onClick={() => (spatial ? dollyCam(1.25) : zoomBy(0.8, cx, stageH / 2))}
          />
          <DockBtn
            name="fit"
            label={spatial ? 'Frame the whole graph' : 'Fit the whole graph'}
            theme={theme}
            onClick={() => {
              setFocus(null)
              if (spatial) frameCam(stageW, stageH)
              else frame(stageW, stageH)
            }}
          />
          <span
            aria-hidden
            style={{ height: 1, margin: '4px 6px', background: 'var(--hairline-strong)' }}
          />
          <DockBtn
            name="spark"
            label="Guide me through what the run left"
            theme={theme}
            active={coachOpen}
            // Any un-ended phase, matching `openCoach`'s door: lit during a
            // confirm pause, this button's press would refuse and fall into
            // the grow-a-branch sentence below — false on a thirty-node graph.
            disabled={
              session.order.length < 2 ||
              busy ||
              (runPhase !== undefined && runPhase !== 'ended') ||
              rewound
            }
            // The same four conditions, each in the words of what would light it.
            why={
              session.order.length < 2
                ? 'Grow at least one branch first.'
                : runPhase !== undefined && runPhase !== 'ended'
                  ? 'Not while a run is going.'
                  : busy
                    ? 'Not while a pass is in flight.'
                    : rewound
                      ? 'Not while the replay is rewound — go back to now.'
                      : undefined
            }
            tint="var(--accent)"
            onClick={() => {
              // The spark opens the coach now. The refusal is unchanged: the
              // same gate refuses for the same reason, so the same sentence is
              // still the true one.
              if (!openCoach()) {
                toast('Grow at least one branch before asking for a first read.', 'info')
                return
              }
              setRightTab('node')
            }}
          />
          <DockBtn
            name="cube"
            label={
              spatial
                ? 'Back to the flat view'
                : 'Spatial view — height is how much a claim has been checked.'
            }
            keys={{ shown: '3', aria: '3' }}
            theme={theme}
            active={spatial}
            onClick={() => setViewMode(spatial ? '2d' : '3d', stageW, stageH)}
          />
          <DockBtn
            name={theme === 'dark' ? 'sun' : 'moon'}
            label={theme === 'dark' ? 'Light field' : 'Dark field'}
            theme={theme}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          />
          {/* The sheet's rail ends on the eye. The inspector opens from a node
              click or ⌘], focus releases from the inspector's footer — neither
              needs a seat here. */}
          <DockBtn
            name="eye"
            label="What the colors mean — and what is drawn"
            theme={theme}
            active={visOpen}
            opensDialog
            tint="var(--accent)"
            onClick={() => {
              setVisOpen((v) => !v)
              setSpatialNoteSeen(true)
            }}
          />
          {/* The palette, as a button. ⌘K was its only route besides the
              empty-canvas hero — which left touch with no way into the app's
              one universal namespace, and with it no tune panel, no autonomous
              run, no export, no library. The dock is chrome's home, and a
              visible seat also teaches the keystroke it names. */}
          <DockBtn
            name="search"
            label="Search nodes and commands"
            keys={{ shown: '⌘K', aria: 'Meta+K Control+K' }}
            theme={theme}
            onClick={() => setPalette(true)}
          />
        </GlassShell>

        {/* How many marks the switches are hiding, worn on the cluster's own
            shoulder — the popover below says which switches, and the refuted
            line inside says when it matters. A button, and a way back (D335):
            it used to be a span that took no pointer, so its title never showed,
            a press did nothing, and a screen reader heard a bare number for a
            state that persists across reloads and every exploration. Pressing it
            opens the legend, where "Show all" is one press. "The key" would have
            read as an API key, which is what the word means everywhere else. */}
        {switchedOff > 0 && (
          <button
            type="button"
            className="num tooldock-badge"
            aria-label={`${switchedOff} hidden by display switches — show what the colors mean`}
            title={`${switchedOff} node${switchedOff === 1 ? ' is' : 's are'} switched off — their branches go with them.`}
            onClick={() => {
              setVisOpen(true)
              setSpatialNoteSeen(true)
            }}
            style={{
              position: 'absolute',
              top: -7,
              right: -7,
              minWidth: 19,
              height: 19,
              padding: '0 5px',
              borderRadius: 999,
              display: 'grid',
              placeItems: 'center',
              background: 'var(--accent)',
              // The token, not white. This badge is a solid accent fill, and the
              // accent is now brass on the night field — white on brass reads at
              // 2.15:1, which was already failing when the accent was blue and
              // fails further here. `--text-inverse` is the colour each medium
              // reserves for sitting ON a filled accent: near-black on brass,
              // white on the plate's deep bistre.
              color: 'var(--text-inverse)',
              fontSize: 'var(--fs-micro)',
              fontWeight: 640,
              // The knockout ring is the field's own ground, tokenized — the
              // old literal imitated it by hand with a +6 blue cast.
              boxShadow: '0 0 0 2px var(--field-ring)',
              cursor: 'pointer',
              zIndex: 2,
            }}
          >
            {switchedOff}
          </button>
        )}

        {/* The legend, anchored off the cluster's left shoulder: what each
            colour means, how many nodes wear it, and its switch. The same
            filters the bottom bar's key toggles — this is the full set with
            words, that is the inline shorthand.

            It is centred on the dock, so every row it grows costs it headroom
            at BOTH ends — the marks section took it past a laptop viewport. It
            scrolls now rather than clipping, on the app's own thin bar.

            Not on a phone, where the rail itself is hung from the top: centred
            on it, the legend's head stood above the screen's edge, behind the
            sidebar's opener. It hangs from the rail's top there, and runs down
            to the composer's band and no further. Its class is a name the
            stylesheet reads: while it is up, the run-done column steps under
            this layer (global.css, `.rundonecol`). */}
        {visOpen && (
          <div
            role="dialog"
            aria-label="What the colors mean — and what is drawn"
            className="scroll tooldock-legend"
            style={{
              position: 'absolute',
              ...(narrow
                ? {
                    top: 0,
                    transform: 'none',
                    maxHeight: Math.max(
                      160,
                      Math.min(560, stageH - NARROW_TOP - viewInset.bottom - 16),
                    ),
                  }
                : { top: '50%', transform: 'translateY(-50%)', maxHeight: 'min(560px, 78vh)' }),
              right: 'calc(100% + 10px)',
              width: 268,
              // No fill override. This popover used to carry its own
              // near-opaque slab, with the honest comment that switch labels
              // read through the ambient 6% fill as two texts fighting — which
              // was true, and is now the reason --glass-fill is what it is.
              // The rule holds for every text-bearing surface by default, so
              // the four hand-patched literals could go. See tokens.css.
              ...glass(),
              borderRadius: 'var(--r-glass)',
              padding: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              textAlign: 'left',
            }}
          >
            {/* First, because it is the correction. Everything under it is true
                of both views, and somebody who opened this popover in the
                spatial view opened it to find out why a node got bigger. In
                --text-2 rather than the rows' --text-3: it is a re-binding, not
                a footnote. */}
            {spatial && (
              <>
                <span style={{ ...EYEBROW, padding: '6px 10px 4px' }}>In the spatial view</span>
                <p
                  style={{
                    margin: '0 10px 6px',
                    fontSize: 'var(--fs-micro)',
                    fontWeight: 300,
                    lineHeight: 1.45,
                    color: 'var(--text-2)',
                    textWrap: 'pretty',
                  }}
                >
                  {SPATIAL_REBIND}
                </p>
              </>
            )}
            {/* Every row below is also a switch, so a reader who pressed one to
                learn what it meant hid it — in every exploration, past a reload.
                The way back is one press on the heading's own line, beside the
                rows it restores (D335), and only while one of them is off. */}
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ ...EYEBROW, flex: 1, padding: '6px 10px 4px' }}>
                What the colors mean
              </span>
              {channelsOff && (
                <button
                  type="button"
                  className="lnk tooldock-showall"
                  title="Turn every colour back on. Signal flow and labels stay as they are."
                  onClick={showAllChannels}
                  style={{ fontSize: 'var(--fs-micro)', marginRight: 10 }}
                >
                  Show all
                </button>
              )}
            </span>
            {LEGEND.map((row) => {
              const on = filters[row.filter]
              return (
                <Fragment key={row.ch}>
                  <button
                    onClick={() => toggleFilter(row.filter)}
                    aria-pressed={on}
                    title={legendToggleTitle(row, on)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 9,
                      padding: '7px 10px',
                      borderRadius: 'var(--r-md)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      opacity: on ? 1 : 0.55,
                      transition: 'opacity 130ms ease',
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        marginTop: 3,
                        borderRadius: '50%',
                        flex: 'none',
                        background: on ? `var(--sem-${row.ch})` : 'transparent',
                        border: on ? 'none' : `1px solid var(--sem-${row.ch})`,
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span
                          className="num"
                          style={{
                            flex: 1,
                            fontSize: 'var(--fs-micro)',
                            fontWeight: 590,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'var(--text-1)',
                          }}
                        >
                          {row.label}
                        </span>
                        <span
                          className="num"
                          style={{ fontSize: 'var(--fs-micro)', color: 'var(--text-3)' }}
                        >
                          {perChannel[row.ch]}
                        </span>
                        <span
                          className="num"
                          style={{
                            fontSize: '9.5px',
                            letterSpacing: '0.1em',
                            color: on ? 'var(--ink-insight)' : 'var(--text-3)',
                          }}
                        >
                          {on ? 'ON' : 'OFF'}
                        </span>
                      </span>
                      <span
                        style={{
                          display: 'block',
                          marginTop: 2,
                          fontSize: 'var(--fs-micro)',
                          fontWeight: 300,
                          lineHeight: 1.4,
                          color: 'var(--text-3)',
                          textWrap: 'pretty',
                        }}
                      >
                        {row.d}
                      </span>
                    </span>
                  </button>
                  {/* Under the Disputed row when that is the switch hiding them,
                    so the warning reads against the press that fixes it. */}
                  {row.filter === 'disputed' && !on && disputedWarning}
                </Fragment>
              )
            })}
            {filters.disputed && disputedWarning}
            <span style={{ ...EYEBROW, padding: '8px 10px 4px' }}>Canvas</span>
            <Switch
              label="Signal flow"
              checked={filters.particles}
              onChange={() => toggleFilter('particles')}
              tint="var(--text-2)"
            />
            <Switch
              label="Labels"
              checked={filters.labels}
              onChange={() => toggleFilter('labels')}
              tint="var(--text-2)"
            />
            {/* Not a filter: nothing appears or disappears. It changes what
                SIZES a claim — the model's own ratings, or the records about it
                — and `core/weight.ts` names the terms. */}
            <Switch
              label="Weight by evidence"
              checked={weighting === 'evidence'}
              onChange={() => setWeighting(weighting === 'evidence' ? 'model' : 'evidence')}
              tint="var(--ink-insight)"
            />
            {weighting === 'evidence' && (
              <p
                style={{
                  margin: '4px 10px 6px',
                  fontSize: 'var(--fs-micro)',
                  lineHeight: 1.5,
                  color: 'var(--text-3)',
                  textWrap: 'pretty',
                }}
              >
                Strand thickness and node size now come from what has been established — a
                supporting source check, a pass from another provider, an agreeing panel, a recorded
                outcome — and the halo only from novelty a search measured. The model’s own ratings
                are unchanged on every card.
              </p>
            )}
            {/* The other half of the eye's own name. Below the switches, and
                behind a chevron so the popover opens at the height it always
                opened at. */}
            <button
              aria-expanded={marksOpen}
              aria-controls={marksId}
              onClick={() => setMarksOpen((v) => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '9px 10px 5px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ ...EYEBROW, flex: 1 }}>What the marks mean</span>
              <Icon
                name="chevron"
                size={9}
                style={{
                  flex: 'none',
                  color: 'var(--text-3)',
                  transform: marksOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </button>
            {marksOpen && (
              <div id={marksId} ref={marksRef}>
                {MARKS.map((group) => (
                  <div key={group.head}>
                    <span style={MARK_GROUP}>{group.head}</span>
                    {group.rows.map((row) => (
                      <div
                        key={row.name}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 9,
                          padding: '5px 10px',
                        }}
                      >
                        <Mark name={row.name} tint={row.tint} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            className="num"
                            style={{
                              display: 'block',
                              fontSize: 'var(--fs-micro)',
                              fontWeight: 590,
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              color: 'var(--text-1)',
                            }}
                          >
                            {row.label}
                          </span>
                          <span
                            style={{
                              display: 'block',
                              marginTop: 2,
                              fontSize: 'var(--fs-micro)',
                              fontWeight: 300,
                              lineHeight: 1.4,
                              color: 'var(--text-3)',
                              textWrap: 'pretty',
                            }}
                          >
                            {row.d}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* The re-binding, once, for somebody who switched views and never
            opens the eye. Hung BELOW the cluster and positioned absolutely, so
            it neither re-centres the rail under the user's cursor nor lands
            over the middle of the field; on the lighter transient glass, like
            every other layer that is passing through. */}
        {spatial && !spatialNoteSeen && (
          <div
            role="status"
            style={{
              position: 'absolute',
              top: 'calc(100% + 12px)',
              right: 0,
              width: 244,
              ...glass(),
              borderRadius: 'var(--r-glass)',
              padding: '9px 9px 10px 11px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
              textAlign: 'left',
            }}
          >
            <p
              style={{
                margin: 0,
                flex: 1,
                minWidth: 0,
                fontSize: 'var(--fs-micro)',
                fontWeight: 300,
                lineHeight: 1.45,
                color: 'var(--text-2)',
                textWrap: 'pretty',
              }}
            >
              {SPATIAL_CAPTION}
            </p>
            {/* Drawn at 18px; on a coarse pointer the target grows under it to
                40 (`.tooldock-dismiss`), as the coach's Skip does (D335). */}
            <button
              className="tooldock-dismiss"
              onClick={() => setSpatialNoteSeen(true)}
              aria-label="Dismiss"
              title="Dismiss"
              style={{
                position: 'relative',
                flex: 'none',
                display: 'grid',
                placeItems: 'center',
                width: 18,
                height: 18,
                borderRadius: 6,
                color: 'var(--text-3)',
                cursor: 'pointer',
              }}
            >
              <Icon name="close" size={13} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
